import React, { useMemo } from 'react';
import Icon from '../../../components/AppIcon';

const AchievementTimeline = ({ sections, documents }) => {
  // Calculate achievement stats
  const stats = useMemo(() => {
    const totalDocuments = Object.values(documents || {}).reduce((sum, docs) => sum + (docs?.length || 0), 0);
    const completedSections = sections?.filter(s => s?.isComplete)?.length || 0;
    const totalSections = sections?.length || 0;
    
    return {
      totalDocuments,
      completedSections,
      totalSections,
      progressPercent: Math.round((completedSections / totalSections) * 100),
    };
  }, [sections, documents]);

  // Milestones with celebration moments
  const milestones = [
    {
      name: 'Kickoff',
      description: 'Portfolio started',
      icon: 'Rocket',
      condition: () => stats.totalDocuments >= 0,
      emoji: '🚀'
    },
    {
      name: 'First Document',
      description: 'First upload complete',
      icon: 'Upload',
      condition: () => stats.totalDocuments >= 1,
      emoji: '📄'
    },
    {
      name: 'Building Momentum',
      description: '3+ documents uploaded',
      icon: 'Zap',
      condition: () => stats.totalDocuments >= 3,
      emoji: '⚡'
    },
    {
      name: 'Half-way There',
      description: '50% sections complete',
      icon: 'Target',
      condition: () => stats.progressPercent >= 50,
      emoji: '🎯'
    },
    {
      name: 'Almost Master',
      description: '75% sections complete',
      icon: 'Award',
      condition: () => stats.progressPercent >= 75,
      emoji: '🏅'
    },
    {
      name: 'Portfolio Legend',
      description: 'All sections completed!',
      icon: 'Crown',
      condition: () => stats.progressPercent === 100,
      emoji: '👑'
    }
  ];

  const activeMilestones = milestones.filter(m => m.condition());
  const nextMilestone = milestones.find(m => !m.condition());

  // Document breakdown by category
  const categoryBreakdown = Object.entries(documents || {})
    .filter(([_, docs]) => docs?.length > 0)
    .map(([category, docs]) => ({
      category,
      count: docs.length,
      icons: ['Academic', 'Achievements', 'Volunteering', 'Skills', 'Extracurricular']
    }));

  return (
    <div className="bg-card border border-border rounded-lg p-6 sticky top-20">
      {/* Hero Section */}
      <div className="mb-8">
        <div className="relative w-full bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-6 overflow-hidden">
          <div className="absolute top-0 right-0 opacity-10">
            <Icon name="Sparkles" size={80} color="var(--color-primary)" />
          </div>
          <div className="relative z-10">
            <div className="text-4xl font-bold text-foreground mb-2">
              {stats.progressPercent}%
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Portfolio Complete
            </p>
            
            {/* Progress Bar */}
            <div className="w-full bg-muted rounded-full h-2 overflow-hidden">
              <div 
                className="bg-gradient-to-r from-primary to-accent h-full rounded-full transition-all duration-500"
                style={{ width: `${stats.progressPercent}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-3 mb-8">
        <div className="text-center p-3 bg-muted/50 rounded-lg">
          <div className="text-xl font-bold text-primary">{stats.totalDocuments}</div>
          <p className="text-xs text-muted-foreground">Documents</p>
        </div>
        <div className="text-center p-3 bg-muted/50 rounded-lg">
          <div className="text-xl font-bold text-accent">{stats.completedSections}</div>
          <p className="text-xs text-muted-foreground">Sections</p>
        </div>
        <div className="text-center p-3 bg-muted/50 rounded-lg">
          <div className="text-xl font-bold text-success">{activeMilestones.length}</div>
          <p className="text-xs text-muted-foreground">Milestones</p>
        </div>
      </div>

      {/* Achievement Timeline */}
      <div className="mb-8 pt-6 border-t border-border">
        <h4 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="TrendingUp" size={16} color="var(--color-primary)" />
          Achievement Timeline
        </h4>
        
        <div className="relative">
          {/* Timeline Line */}
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gradient-to-b from-primary to-accent" />
          
          {/* Milestones */}
          <div className="space-y-3">
            {activeMilestones.map((milestone, idx) => (
              <div key={idx} className="pl-16 pb-2">
                <div className="absolute left-0 w-9 h-9 bg-card rounded-full border-2 border-primary flex items-center justify-center">
                  <span className="text-lg">{milestone.emoji}</span>
                </div>
                <div className="bg-success/5 border border-success/20 rounded-lg p-3">
                  <p className="text-sm font-medium text-foreground">{milestone.name}</p>
                  <p className="text-xs text-muted-foreground">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Next Milestone */}
      {nextMilestone && (
        <div className="pt-6 border-t border-border">
          <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="Flag" size={16} color="var(--color-accent)" />
            Next Goal
          </h4>
          <div className="bg-accent/5 border border-accent/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="text-2xl">{nextMilestone.emoji}</div>
              <div>
                <p className="font-medium text-foreground text-sm">{nextMilestone.name}</p>
                <p className="text-xs text-muted-foreground mt-1">{nextMilestone.description}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Celebration Message */}
      {stats.progressPercent === 100 && (
        <div className="mt-6 p-4 bg-gradient-to-r from-success/10 to-primary/10 border border-success/30 rounded-lg text-center">
          <p className="text-sm font-semibold text-success mb-1">🎉 Congratulations! 🎉</p>
          <p className="text-xs text-muted-foreground">Your portfolio is complete and ready to shine</p>
        </div>
      )}
    </div>
  );
};

export default AchievementTimeline;
